import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { app, functions } from '../firebase.config';
import { getTenantId, getStoreId } from './userStoreCache';

// Firebase Cloud Messaging Service
// Handles FCM token registration and notification handling
//
// Tokens are stored server-side in `pushTokens/{token}` keyed by Firebase Auth
// uid (see the registerPushToken callable). They are NOT written to the
// tenant/store users doc: those are keyed by generated ids like
// user_Fritz_1699..._ab12cde, not by uid, so most staff have no users/{uid}
// document at all -- and the uid is what the inbound-message trigger has to
// look up when routing a customer's reply back to whoever texted them.

let messaging = null;

// Firebase Messaging VAPID key (Firebase Console > Project Settings > Cloud Messaging)
const VAPID_KEY =
  'BKnfz48PkYOBwztsd5-oO5SoPg_6H1E2wCozjtfqhEdfbZvcn4WYVVw0ZPA0eDFuU2u49D9btxtE6kqL6xiC_3w';

/**
 * True when the app is running as an installed app rather than a browser tab.
 * iOS only exposes Web Push to Home Screen web apps, so this is the difference
 * between notifications working and the API not existing at all.
 */
export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
};

/**
 * Whether this browser can receive push at all.
 *
 * isSupported() does the real work -- it checks for ServiceWorker, PushManager
 * and Notification. On iOS Safari in a normal tab those are absent, so it
 * returns false without us needing to sniff the user agent. (The old
 * user-agent test here blocked every Safari version, including the iOS 16.4+
 * and macOS 16.1+ ones that do support Web Push.)
 */
export const isPushSupported = async () => {
  try {
    return await isSupported();
  } catch (error) {
    console.warn('⚠️ Push support check failed:', error);
    return false;
  }
};

// Initialize Firebase Messaging
const initializeMessaging = async () => {
  if (messaging) return messaging;

  if (!(await isPushSupported())) {
    console.log('ℹ️ Push messaging is not supported in this browser');
    return null;
  }

  try {
    messaging = getMessaging(app);
    console.log('✅ Firebase Messaging initialized');
    return messaging;
  } catch (error) {
    console.error('❌ Error initializing Firebase Messaging:', error);
    return null;
  }
};

/**
 * Request notification permission and get FCM token
 *
 * Must be called from a user gesture (a tap). iOS rejects
 * Notification.requestPermission() outside one, silently.
 *
 * @returns {Promise<string|null>} FCM token or null if unavailable
 */
export const requestNotificationPermission = async () => {
  try {
    const msg = await initializeMessaging();
    if (!msg) return null;

    if (Notification.permission === 'denied') {
      console.warn('⚠️ Notification permission was denied by user');
      return null;
    }

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('⚠️ Notification permission not granted');
        return null;
      }
    }

    console.log('✅ Notification permission granted');

    const token = await getToken(msg, { vapidKey: VAPID_KEY });

    if (!token) {
      console.warn('⚠️ No FCM token available');
      return null;
    }

    return token;
  } catch (error) {
    console.error('❌ Error getting FCM token:', error);
    return null;
  }
};

/**
 * Save the device token against the signed-in user, server-side.
 * @param {string} token - FCM token
 */
export const saveFCMToken = async (token) => {
  const registerPushToken = httpsCallable(functions, 'registerPushToken');

  await registerPushToken({
    token,
    tenantId: getTenantId() || null,
    storeId: getStoreId() || null,
    platform: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  });

  console.log('✅ Push token registered');
};

/**
 * Register this device for push notifications.
 * Call from a tap handler.
 * @returns {Promise<boolean>} whether the device is now registered
 */
export const registerForNotifications = async () => {
  try {
    console.log('📱 Registering device for push notifications...');

    const token = await requestNotificationPermission();
    if (!token) return false;

    await saveFCMToken(token);

    console.log('✅ Device registered for push notifications');
    return true;
  } catch (error) {
    console.error('❌ Error registering for notifications:', error);
    return false;
  }
};

/**
 * Setup foreground message listener
 * Handles notifications when app is in foreground
 */
export const setupForegroundListener = (onMessageCallback) => {
  let unsubscribe = null;
  let cancelled = false;

  initializeMessaging().then((msg) => {
    if (!msg || cancelled) return;

    unsubscribe = onMessage(msg, (payload) => {
      console.log('🔔 Foreground notification received:', payload);

      // Device-local mute. Note this only reaches foreground notifications --
      // the service worker can't read localStorage, so background pushes are
      // unaffected by this toggle.
      const isMuted = localStorage.getItem('notificationsMuted') === 'true';
      if (isMuted) {
        console.log('🔕 Notification muted by user preference');
        return;
      }

      const { notification, data } = payload;

      if (notification) {
        new Notification(notification.title || 'New Notification', {
          body: notification.body,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          tag: data?.phone ? `conversation-${data.phone}` : Date.now().toString(),
          requireInteraction: false,
          data: data,
        });
      }

      if (onMessageCallback && typeof onMessageCallback === 'function') {
        onMessageCallback(payload);
      }
    });
  });

  return () => {
    cancelled = true;
    if (unsubscribe) unsubscribe();
  };
};

/**
 * Stop sending push to this device.
 */
export const disableNotifications = async () => {
  try {
    const msg = await initializeMessaging();
    if (!msg) return;

    const token = await getToken(msg, { vapidKey: VAPID_KEY }).catch(() => null);

    if (token) {
      const unregisterPushToken = httpsCallable(functions, 'unregisterPushToken');
      await unregisterPushToken({ token });
      await deleteToken(msg).catch(() => {});
    }

    console.log('✅ Notifications disabled');
  } catch (error) {
    console.error('❌ Error disabling notifications:', error);
    throw error;
  }
};

/**
 * Check if notifications are enabled
 */
export const areNotificationsEnabled = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'granted';
};

export default {
  isPushSupported,
  isStandalone,
  requestNotificationPermission,
  saveFCMToken,
  registerForNotifications,
  setupForegroundListener,
  disableNotifications,
  areNotificationsEnabled,
};
